import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Spinner,
  Button,
  Alert,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import { postRequest } from "../../components/Common/Utils.js";

export default function WarehouseManagementCreate() {
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationBlocked, setLocationBlocked] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const history = useHistory();

  const [warehouseData, setWarehouseData] = useState({
    name: "",
    address: "",
    latitude: "",
    longitude: "",
    phone: "",
    capacity: "",
    country: "",
    country_code: "",
    city: "",
    state: "",
    postCode: "",
  });

  // Initialize Google Maps
  useEffect(() => {
    if (showMap && mapRef.current && !map) {
      const google = window.google;
      const newMap = new google.maps.Map(mapRef.current, {
        center: { lat: 0, lng: 0 },
        zoom: 2,
      });
      setMap(newMap);

      // Add click listener
      newMap.addListener("click", (e) => {
        setWarehouseData((prev) => ({
          ...prev,
          latitude: e.latLng.lat().toString(),
          longitude: e.latLng.lng().toString(),
        }));

        if (marker) {
          marker.setMap(null);
        }

        const newMarker = new google.maps.Marker({
          position: e.latLng,
          map: newMap,
        });
        setMarker(newMarker);
      });
    }
  }, [showMap, map, marker]);

  // Initialize Autocomplete
  useEffect(() => {
    if (window.google && !autocompleteRef.current) {
      const input = document.getElementById("address-input");
      if (input) {
        const autocomplete = new window.google.maps.places.Autocomplete(input, {
          types: ["geocode"],
        });
        autocompleteRef.current = autocomplete;

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (place.geometry) {
            // Extract address components
            let country = "";
            let country_code = "";
            let city = "";
            let state = "";
            let postCode = "";

            for (const component of place.address_components) {
              const componentType = component.types[0];

              if (componentType === "country") {
                country = component.long_name;
                country_code = component.short_name;
              }

              if (componentType === "locality") {
                city = component.long_name;
              }

              if (componentType === "administrative_area_level_1") {
                state = component.long_name;
              }

              if (componentType === "postal_code") {
                postCode = component.long_name;
              }
            }

            setWarehouseData((prev) => ({
              ...prev,
              address: place.formatted_address,
              latitude: place.geometry.location.lat().toString(),
              longitude: place.geometry.location.lng().toString(),
              country,
              country_code,
              city,
              state,
              postCode,
            }));

            if (map) {
              map.setCenter(place.geometry.location);
              map.setZoom(15);

              if (marker) {
                marker.setMap(null);
              }

              const newMarker = new window.google.maps.Marker({
                position: place.geometry.location,
                map: map,
              });
              setMarker(newMarker);
            }
          }
        });
      }
    }
  }, [map, marker]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      name: warehouseData.name,
      address: warehouseData.address,
      latitude: parseFloat(warehouseData.latitude),
      longitude: parseFloat(warehouseData.longitude),
      phone: parseInt(warehouseData.phone),
      capacity: parseInt(warehouseData.capacity),
      country: warehouseData.country,
      country_code: warehouseData.country_code,
      city: warehouseData.city,
      state: warehouseData.state,
      postCode: warehouseData.postCode,
    };

    try {
      const response = await postRequest("/warehouse/add", payload);
      setLoading(false);

      if (response.status) {
        toast.success(response.message);
        setTimeout(() => {
          history.goBack();
        }, 1000);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      setLoading(false);
      // toast.error("An error occurred while creating the warehouse");
      // console.error("Error:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setWarehouseData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleMap = () => {
    setShowMap(!showMap);
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <Row>
        <Col className="10">
          <div className="page-title-box">
            <h4>Warehouse Management</h4>
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item active">Warehouse Management</li>
              <li className="breadcrumb-item">Create Warehouse</li>
            </ol>
          </div>
        </Col>

        <div class="col-md-2 text-end">
          <button
            type="button"
            onClick={() => history.goBack()}
            className="btn btn-primary btn-lg"
          >
            <i class="fas fa-chevron-left"></i> Back
          </button>
        </div>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            {loading ? (
              <CardBody style={{ height: "100px" }}>
                <Spinner
                  color="info"
                  style={{
                    height: "4rem",
                    width: "4rem",
                    position: "absolute",
                    left: "50%",
                  }}
                >
                  Loading...
                </Spinner>
              </CardBody>
            ) : (
              <CardBody>
                <h4 className="card-title">Create Warehouse</h4>

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col lg={12} className="ms-lg-auto">
                      <div className="mt-5 mt-lg-4">
                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Warehouse Name
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="name"
                              onChange={handleChange}
                              value={warehouseData.name}
                              className="form-control"
                              placeholder="Enter warehouse name"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Address
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              id="address-input"
                              name="address"
                              onChange={handleChange}
                              value={warehouseData.address}
                              className="form-control"
                              placeholder="Enter full street address"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Country
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="country"
                              onChange={handleChange}
                              value={warehouseData.country}
                              className="form-control"
                              placeholder="Enter country name"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Country Code
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="country_code"
                              onChange={handleChange}
                              value={warehouseData.country_code}
                              className="form-control"
                              placeholder="Enter country code"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            City
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="city"
                              onChange={handleChange}
                              value={warehouseData.city}
                              className="form-control"
                              placeholder="Enter city name"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            State
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="state"
                              onChange={handleChange}
                              value={warehouseData.state}
                              className="form-control"
                              placeholder="Enter state"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Postal Code
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="postCode"
                              onChange={handleChange}
                              value={warehouseData.postCode}
                              className="form-control"
                              placeholder="Enter postal/zip code"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Location
                          </label>
                          <div className="col-sm-6">
                            {/* <div className="d-flex mb-2 gap-2">
                              <Button color="secondary" onClick={toggleMap}>
                                {showMap ? "Hide Map" : "Show Map"}
                              </Button>
                            </div>

                            {locationBlocked && (
                              <Alert color="danger" className="mb-3">
                                <strong>Location access blocked</strong>
                                <p className="mb-1">
                                  You've previously denied location access. To
                                  reset:
                                </p>
                                <ol className="small">
                                  <li>
                                    Click the lock icon in your browser's
                                    address bar
                                  </li>
                                  <li>
                                    Go to "Site settings" or "Permissions"
                                  </li>
                                  <li>Find "Location" and change to "Allow"</li>
                                  <li>Refresh this page</li>
                                </ol>
                              </Alert>
                            )}

                            {showMap && (
                              <div
                                ref={mapRef}
                                style={{
                                  height: "300px",
                                  width: "100%",
                                  marginBottom: "1rem",
                                }}
                              />
                            )} */}

                            <div className="row">
                              <div className="col-md-6">
                                <label>Latitude</label>
                                <input
                                  type="number"
                                  step="any"
                                  name="latitude"
                                  onChange={handleChange}
                                  value={warehouseData.latitude}
                                  className="form-control"
                                  required
                                  placeholder="Click on map or search address to auto-fill"
                                />
                              </div>
                              <div className="col-md-6">
                                <label>Longitude</label>
                                <input
                                  type="number"
                                  step="any"
                                  name="longitude"
                                  onChange={handleChange}
                                  value={warehouseData.longitude}
                                  className="form-control"
                                  required
                                  placeholder="Click on map or search address to auto-fill"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Phone Number
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="number"
                              name="phone"
                              onChange={handleChange}
                              value={warehouseData.phone}
                              className="form-control"
                              placeholder="Enter contact number"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Capacity
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="number"
                              name="capacity"
                              onChange={handleChange}
                              value={warehouseData.capacity}
                              className="form-control"
                              placeholder="Enter storage capacity in units"
                              required
                            />
                          </div>
                        </div>

                        <div className="mt-5 mt-lg-4">
                          <div className="row justify-content-end">
                            <div className="col-sm-9">
                              <div>
                                <button
                                  type="submit"
                                  className="btn btn-primary w-md"
                                >
                                  Submit
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            )}
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
}
